import { useEffect, useRef } from 'react';

export const useDebouncedCallback = <T extends (...args: never[]) => void>(callback: T, delay: number): T => {
    const callbackRef = useRef(callback);
    const delayRef = useRef(delay);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    // Read at call time, not capture time, so a caller can vary the delay between renders
    // (e.g. no debounce on the first, uninitialized fetch).
    useEffect(() => {
        delayRef.current = delay;
    }, [delay]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return useRef((...args: Parameters<T>) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            callbackRef.current(...args);
        }, delayRef.current);
    }).current as T;
};
